import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Plus, Trash2, ChevronUp, ChevronDown, Save, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';

const DEFAULT_FIELDS = [
  { fieldId: 'name', label: 'Full Name', fieldType: 'text', required: true, isDefault: true, isDeletable: true },
  { fieldId: 'email', label: 'Email Address', fieldType: 'email', required: true, isDefault: true, isDeletable: false },
  { fieldId: 'phone', label: 'Phone Number', fieldType: 'phone', required: true, isDefault: true, isDeletable: true },
];

const ApplyLinkFieldsAdmin = ({ orgId }) => {
  const [fields, setFields] = useState([]);
  const [companyDescription, setCompanyDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasOverrides, setHasOverrides] = useState(false);
  // Raw text for the "Options (comma-separated)" inputs while the admin is typing, keyed by
  // fieldId. Parsing to an array on every keystroke would strip the comma the user just typed
  // (split→filter removes the trailing empty segment), so we keep the raw text until blur/save.
  const [optionsDrafts, setOptionsDrafts] = useState({});

  // Parse a raw "a, b, c" string into a clean options array.
  const parseOptions = (raw) =>
    String(raw || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

  // Merge any in-progress option drafts into their fields' options arrays.
  const flushOptionDrafts = (list) =>
    list.map((f) =>
      Object.prototype.hasOwnProperty.call(optionsDrafts, f.fieldId)
        ? { ...f, options: parseOptions(optionsDrafts[f.fieldId]) }
        : f
    );

  useEffect(() => {
    loadFields();
  }, [orgId]);

  const loadFields = async () => {
    setLoading(true);
    try {
      // Fetch raw config to see if they have overrides
      const [rawRes, detailRes] = await Promise.all([
        axios.get(`/api/internal/org-config/${orgId}/raw`),
        axios.get(`/api/internal/org-detail/${orgId}`),
      ]);
      const rawFields = rawRes.data?.config?.registrationFields;
      if (rawFields) {
        setHasOverrides(true);
        setFields(rawFields);
      } else {
        setHasOverrides(false);
        setFields([...DEFAULT_FIELDS]);
      }
      setCompanyDescription(detailRes.data?.subscription?.publicDescription || '');
    } catch (err) {
      console.error(err);
      toast.error('Failed to load apply link fields');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (fieldsToSave = fields) => {
    setSaving(true);
    try {
      // Flush any unblurred option drafts so the latest typed options are persisted.
      const flushed = flushOptionDrafts(fieldsToSave);
      await Promise.all([
        axios.put(`/api/internal/org-config/${orgId}`, { registrationFields: flushed }),
        axios.patch(`/api/internal/org/${orgId}/profile`, { publicDescription: companyDescription }),
      ]);
      toast.success('Configuration saved successfully');
      setHasOverrides(true);
      setFields(flushed);
      setOptionsDrafts({});
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!window.confirm('Reset to platform defaults? This will remove all custom fields.')) return;
    setSaving(true);
    try {
      await axios.put(`/api/internal/org-config/${orgId}`, { registrationFields: null });
      toast.success('Reset to defaults');
      setHasOverrides(false);
      setFields([...DEFAULT_FIELDS]);
      setOptionsDrafts({});
    } catch (err) {
      toast.error('Failed to reset fields');
    } finally {
      setSaving(false);
    }
  };

  const moveField = (idx, dir) => {
    if (idx + dir < 0 || idx + dir >= fields.length) return;
    const newFields = [...fields];
    const temp = newFields[idx];
    newFields[idx] = newFields[idx + dir];
    newFields[idx + dir] = temp;
    setFields(newFields);
  };

  const removeField = (idx) => {
    if (!fields[idx].isDeletable && fields[idx].isDefault) {
      toast.error('Cannot delete this default field');
      return;
    }
    const newFields = [...fields];
    newFields.splice(idx, 1);
    setFields(newFields);
  };

  const addField = () => {
    setFields([...fields, {
      fieldId: `custom_${Date.now()}`,
      label: 'New Field',
      fieldType: 'text',
      required: false,
      placeholder: '',
      options: [],
      isDefault: false,
      isDeletable: true,
      order: fields.length
    }]);
  };

  const updateField = (idx, key, value) => {
    const newFields = [...fields];
    const updated = { ...newFields[idx], [key]: value };
    
    if (key === 'fieldType') {
      if (value === 'age' && updated.label === 'New Field') {
        updated.label = 'Age';
      }
      if (value === 'gender') {
        if (updated.label === 'New Field') {
          updated.label = 'Gender';
        }
        if (!updated.options || updated.options.length === 0) {
          updated.options = ['Male', 'Female', 'Non-binary', 'Other', 'Prefer not to say'];
          setOptionsDrafts(prev => ({...prev, [updated.fieldId]: updated.options.join(', ')}))
        }
      }
    }
    
    newFields[idx] = updated;
    setFields(newFields);
  };

  if (loading) {
    return <div className="p-4 text-center text-sm text-gray-500">Loading fields...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Apply Link Fields & Profile</h3>
          <p className="text-xs text-gray-500 mt-1">
            {hasOverrides ? 'Using custom configuration for this organization.' : 'Using standard platform defaults.'}
          </p>
        </div>
        <div className="flex gap-2">
          {hasOverrides && (
            <button onClick={handleResetToDefault} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
              <RefreshCw className="h-3.5 w-3.5" /> Reset to Defaults
            </button>
          )}
          <button onClick={() => handleSave(fields)} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#007BFF] text-white rounded-lg hover:bg-[#0069d9]">
            <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
        <label className="block text-sm font-semibold text-gray-800 mb-2">Company Profile (About Company)</label>
        <p className="text-xs text-gray-500 mb-3">
          This text appears on the left side of the candidate apply page, right before the &quot;What&apos;s included&quot; section. You can use multiple paragraphs or bullet points.
        </p>
        <textarea
          value={companyDescription}
          onChange={(e) => setCompanyDescription(e.target.value)}
          rows={4}
          placeholder="e.g. We are a fast growing startup..."
          className="w-full border-gray-300 rounded-md text-sm p-3 focus:ring-1 focus:ring-[#007BFF] focus:border-[#007BFF]"
        />
      </div>

      <div className="space-y-3">
        {fields.map((f, idx) => (
          <div key={f.fieldId || idx} className="p-4 bg-white border border-gray-200 rounded-lg flex items-start gap-4 shadow-sm">
            <div className="flex flex-col gap-1 mt-1 shrink-0">
              <button disabled={idx === 0} onClick={() => moveField(idx, -1)} className="text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
              <button disabled={idx === fields.length - 1} onClick={() => moveField(idx, 1)} className="text-gray-400 hover:text-gray-700 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
            </div>
            
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Field Label</label>
                <input type="text" value={f.label} onChange={(e) => updateField(idx, 'label', e.target.value)} className="w-full border-gray-300 rounded-md text-sm p-2 focus:ring-1 focus:ring-[#007BFF] focus:border-[#007BFF]" />
              </div>
              <div>
                <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Field Type</label>
                <select value={f.fieldType} onChange={(e) => updateField(idx, 'fieldType', e.target.value)} disabled={f.isDefault} className="w-full border-gray-300 rounded-md text-sm p-2 focus:ring-1 focus:ring-[#007BFF] focus:border-[#007BFF] disabled:bg-gray-50 disabled:text-gray-500">
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="dropdown">Dropdown</option>
                  <option value="dropdown_multi">Multi-select Dropdown</option>
                  <option value="age">Age (Pre-mapped)</option>
                  <option value="dob">Date of Birth (Pre-mapped)</option>
                  <option value="gender">Gender (Pre-mapped)</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Required?</label>
                <select value={f.required ? 'yes' : 'no'} onChange={(e) => updateField(idx, 'required', e.target.value === 'yes')} disabled={f.isDefault && !f.isDeletable} className="w-full border-gray-300 rounded-md text-sm p-2 focus:ring-1 focus:ring-[#007BFF] focus:border-[#007BFF] disabled:bg-gray-50 disabled:text-gray-500">
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div className="flex flex-col justify-end">
                {(!f.isDefault || f.isDeletable) && (
                  <button onClick={() => removeField(idx)} className="self-end inline-flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 py-2">
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
                {f.isDefault && <span className="text-[10px] font-medium text-gray-400 self-end py-2 bg-gray-100 px-2 rounded">Platform Default</span>}
              </div>
              
              {(f.fieldType === 'dropdown' || f.fieldType === 'dropdown_multi' || f.fieldType === 'gender') && (
                <div className="col-span-full">
                  <label className="block text-[10px] uppercase font-semibold text-gray-500 mb-1">Options (comma-separated)</label>
                  <input
                    type="text"
                    value={optionsDrafts[f.fieldId] ?? (f.options || []).join(', ')}
                    onChange={(e) => setOptionsDrafts((prev) => ({ ...prev, [f.fieldId]: e.target.value }))}
                    onBlur={(e) => {
                      updateField(idx, 'options', parseOptions(e.target.value));
                      setOptionsDrafts((prev) => {
                        const next = { ...prev };
                        delete next[f.fieldId];
                        return next;
                      });
                    }}
                    className="w-full border-gray-300 rounded-md text-sm p-2 focus:ring-1 focus:ring-[#007BFF] focus:border-[#007BFF]"
                    placeholder="Option 1, Option 2, Option 3"
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <button onClick={addField} className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-400 transition-colors flex items-center justify-center gap-2">
        <Plus className="h-4 w-4" /> Add Custom Field
      </button>
    </div>
  );
};

export default ApplyLinkFieldsAdmin;
